import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Shield, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import { updateTaskEP } from '../lib/api';

const SIGNAL_COLORS = {
  risk: 'bg-red-50 text-red-700 border-red-200',
  repeat: 'bg-amber-50 text-amber-700 border-amber-200',
  opportunity: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const SIGNAL_LABELS = {
  risk: 'Risk',
  repeat: 'Repeat',
  opportunity: 'Opportunity',
};

const IMPACT_LABELS = {
  access: 'Access',
  comfort: 'Comfort',
  trust: 'Trust',
  revenue: 'Revenue',
  safety: 'Safety',
};

const FIX_LABELS = {
  quick_reply: 'Quick reply',
  guesthub_update: 'GuestHub update',
  maintenance: 'Maintenance',
  policy_decision: 'Policy decision',
  vendor: 'Vendor',
};

const PREVENT_ACTION_OPTIONS = [
  { value: 'save_quick_reply', label: 'Save a quick reply' },
  { value: 'update_guesthub', label: 'Update GuestHub' },
  { value: 'update_policy_note', label: 'Update policy note' },
  { value: 'none', label: 'None / not needed' },
];

export const SignalLine = ({ signal_type, impact_area, fix_route }) => {
  if (!signal_type && !impact_area && !fix_route) return null;
  const sig = signal_type || 'risk';
  const imp = impact_area || 'trust';
  const fix = fix_route || 'quick_reply';

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1.5 mt-1" data-testid="signal-line">
            <Shield className="h-3 w-3 text-stone-400 flex-shrink-0" />
            <span className="text-xs text-stone-500">Signal</span>
            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 h-4 ${SIGNAL_COLORS[sig] || 'bg-stone-50 text-stone-600'}`}>
              {SIGNAL_LABELS[sig] || sig}
            </Badge>
            <span className="text-[10px] text-stone-300">&middot;</span>
            <span className="text-[10px] text-stone-500">{IMPACT_LABELS[imp] || imp}</span>
            <span className="text-[10px] text-stone-300">&middot;</span>
            <span className="text-[10px] text-stone-500">{FIX_LABELS[fix] || fix}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p className="text-xs max-w-[200px]">A quick read on why this matters and how we'll fix it.</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const PreventRepeatModal = ({ open, onOpenChange, taskId, existingNote, existingAction, onSaved }) => {
  const [action, setAction] = useState(existingAction || '');
  const [note, setNote] = useState(existingNote || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateTaskEP(taskId, {
        prevent_repeat_note: note,
        prevent_repeat_action: action || 'none',
      });
      toast.success('Saved. One less repeat for the team.');
      onSaved?.();
      onOpenChange(false);
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="prevent-repeat-modal">
        <DialogHeader>
          <DialogTitle className="font-heading">Prevent repeat</DialogTitle>
          <DialogDescription>
            Capture the small fix now, so we don't answer this again tomorrow.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Best fix</Label>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger data-testid="prevent-repeat-action-select">
                <SelectValue placeholder="Select an action" />
              </SelectTrigger>
              <SelectContent>
                {PREVENT_ACTION_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>What should we change?</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g., Add lockbox steps to GuestHub + save a quick reply."
              rows={3}
              data-testid="prevent-repeat-note-input"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1 rounded-full">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 bg-[#3F617E] hover:bg-[#345470] rounded-full"
              data-testid="prevent-repeat-save-button"
            >
              {saving ? 'Saving...' : 'Save note'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { SIGNAL_COLORS, SIGNAL_LABELS, IMPACT_LABELS, FIX_LABELS };
